import React, { useEffect, useState, useMemo } from 'react';
import {
    Box,
    Typography,
    Button,
    Card,
    Chip,
    Stack,
    Alert,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    CircularProgress,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Paper,
    Radio,
    RadioGroup,
    FormControlLabel,
    FormControl,
    TextField,
    Divider,
    LinearProgress,
    Collapse,
    IconButton,
    Tooltip,
    Grid,
    InputAdornment,
    ToggleButtonGroup,
    ToggleButton
} from '@mui/material';
import {
    CheckCircle as CheckCircleIcon,
    Cancel as CancelIcon,
    Send as SendIcon,
    Person as PersonIcon,
    Assignment as AssignmentIcon,
    Security as SecurityIcon,
    Draw as DrawIcon,
    ExpandMore as ExpandMoreIcon,
    ExpandLess as ExpandLessIcon,
    Warning as WarningIcon,
    Construction as ConstructionIcon,
    Build as BuildIcon,
    Link as LinkIcon,
    HealthAndSafety as SafetyIcon,
    CleaningServices as CleaningIcon,
    Fence as FenceIcon,
    Comment as CommentIcon,
    LocalHospital as InjuryIcon,
    Report as IncidentIcon,
    Description as FormIcon,
    Groups as CrewIcon,
    Scanner as InspectionIcon,
    Handyman as ToolIcon,
    HomeRepairService as RiggingIcon,
    Shield as SafetyEquipmentIcon,
    Home as HousekeepingIcon,
    RadioButtonUnchecked,
    RadioButtonChecked,
    ArrowBack as ArrowBackIcon,
    Mic as MicIcon,
    MicOff as MicOffIcon,
    Close as CloseIcon,
    Email as EmailIcon
} from '@mui/icons-material';
import { MODE_JOB, isLocationMode } from '../utils/stepTwoUtils';
import { useNavigate, useLocation } from 'react-router-dom';
import { initializeDB } from '../../../config/db';
import { JHA_STORE, STATUS_FINISH, STATUS_PROCESS } from '../../../config/constants';
import JhaStepHeader from '../JhaStepHeader';
import SignatureModal from '../SignatureModal';
import SelectSafetyEmployeeModal from '../SelectSafetyEmployeeModal';
import axiosInstance from '../../../axiosInstance';
import buildJhaPayload from '../utils/jhaPayloadAdapter';
import { findMissingDocumentsLocal, isFarpAttachedOrLinked, injectMissingDocRows } from '../utils/documentTriggerEvaluator';
import { showMissingDocsDialog } from '../utils/missingDocsDialog';
import { useRef } from 'react';
import useDictation from '../../../hooks/useDictation';
import Swal from 'sweetalert2';
import fallArrestRescuePlanService from '../../../services/fallArrestRescuePlanService';
import { localDateString } from '../../../utils/localDate';

const TODAY_STRING_S4 = localDateString();

// Swal toast по умолчанию ставит z-index 1060 → перекрывается шапкой (1100).
// Поднимаем выше всех layer'ов.
const TOAST_DEFAULTS = {
    toast: true,
    position: 'top-end',
    showConfirmButton: false,
    didOpen: () => {
        document.querySelectorAll('.swal2-container').forEach((c) => {
            c.style.zIndex = '13000';
        });
    },
};

function getRowSelections(row) {
    if (!row) return [];
    if (Array.isArray(row.energySelections) && row.energySelections.length) {
        return row.energySelections;
    }
    if (row.energyId) {
        return [
            {
                energyId: row.energyId,
                hazards: Array.isArray(row.hazards) ? row.hazards : []
            }
        ];
    }
    return [];
}

const checklistItems = [
    { id: 'injuryFree', label: 'Injury Free', icon: InjuryIcon, color: '#4caf50' },
    { id: 'incidentFree', label: 'Incident Free', icon: IncidentIcon, color: '#4caf50' },
    { id: 'incidentFormCompleted', label: 'Incident/Injury Form Completed', icon: FormIcon, color: '#ff9800' },
    { id: 'planReviewed', label: 'Plan Revised/Reviewed with Crew', icon: CrewIcon, color: '#2196f3' },
    { id: 'equipmentInspected', label: 'Equipment Inspection(s) Completed for shift', icon: InspectionIcon, color: '#9c27b0' },
    { id: 'toolInspected', label: 'Tool Inspection(s) Completed for shift', icon: ToolIcon, color: '#9c27b0' },
    { id: 'riggingInspected', label: 'Rigging Inspection(s) Completed for shift', icon: RiggingIcon, color: '#9c27b0' },
    { id: 'safetyEquipmentInspected', label: 'Safety Equipment Inspection(s) Comp for shift', icon: SafetyEquipmentIcon, color: '#9c27b0' },
    { id: 'barricadesInPlace', label: 'Barricades In Place at end shift', icon: FenceIcon, color: '#ff5722' },
    { id: 'barricadesRemoved', label: 'Barricades Removed at end shift', icon: FenceIcon, color: '#ff5722' },
    { id: 'housekeepingComplete', label: 'Site Housekeeping Complete at end shift', icon: HousekeepingIcon, color: '#607d8b' }
];

export default function JhaStepFour() {
    const { state } = useLocation() || {};
    const { jha = null, viewMode = false } = state || {};
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [jhaData, setJhaData] = useState(null);
    const [submitDialog, setSubmitDialog] = useState(false);
    const [signatureModalOpen, setSignatureModalOpen] = useState(false);
    const [safetyEmployeeModalOpen, setSafetyEmployeeModalOpen] = useState(false);
    const [safetySignatureModalOpen, setSafetySignatureModalOpen] = useState(false);
    const [expandedSections, setExpandedSections] = useState({
        validation: false,
        checklist: true
    });

    // Checklist state
    const [checklist, setChecklist] = useState({});
    const [checklistNotes, setChecklistNotes] = useState({});
    const [comments, setComments] = useState('');
    const [supervisorSignature, setSupervisorSignature] = useState('');

    // Safety Department signature
    const [safetyEmployee, setSafetyEmployee] = useState(null);
    const [safetySignature, setSafetySignature] = useState('');

    // Email functionality state
    const [sendingEmail, setSendingEmail] = useState(false);
    const [emailCooldown, setEmailCooldown] = useState(0);
    const cooldownTimerRef = useRef(null);

    // Voice dictation
    const { isSupported: speechSupported, isListening, start: startDictation, stop: stopDictation } = useDictation();
    
    // Порядок ключей = порядок строк в панели валидации (Object.entries).
    // Шаги 2/3 поменяны местами: 2 = Work Activities, 3 = Documents.
    const [validation, setValidation] = useState({
        general: { valid: false, message: '' },
        activities: { valid: false, message: '' },
        docs: { valid: false, message: '' },
        crew: { valid: false, message: '' },
        review: { valid: false, message: '' }
    });

    // ─── FARP integration (mirror Step3 logic) ───────────────────────────
    // На финальном submit повторяем soft warning из Step3: если есть trigger
    // work activities но FARP не подан — предупреждаем юзера.
    const [farpTriggerValues, setFarpTriggerValues] = useState([]);
    const [farpExistingPlan, setFarpExistingPlan] = useState(null);

    const triggeredActivities = useMemo(() => {
        if (!jhaData?.stepThree?.energyExposures || farpTriggerValues.length === 0) return [];
        const triggerSet = new Set(farpTriggerValues);
        const seen = new Set();
        const result = [];
        for (const row of jhaData.stepThree.energyExposures) {
            const wa = (row.workActivity || '').trim();
            if (wa && triggerSet.has(wa) && !seen.has(wa)) {
                seen.add(wa);
                result.push(wa);
            }
        }
        return result;
    }, [jhaData?.stepThree?.energyExposures, farpTriggerValues]);

    const farpRequired   = triggeredActivities.length > 0;
    const farpJobCode    = jhaData?.stepOne?.jobNumber || '';
    const farpDate       = jhaData?.stepOne?.date || TODAY_STRING_S4;
    const farpJhaLocalId = jhaData?.localId || '';

    // FARP выполнен фото/привязкой/поданной формой — тогда soft-warning «not found»
    // не показываем (фото бланка удовлетворяет требование).
    const farpSatisfied = useMemo(
        () => Boolean(farpExistingPlan) || isFarpAttachedOrLinked(jhaData),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [farpExistingPlan, jhaData?.stepTwo]
    );

    // Load FARP trigger values once (offline-first)
    useEffect(() => {
        (async () => {
            const values = await fallArrestRescuePlanService.getTriggerValues();
            setFarpTriggerValues(values);
        })();
    }, []);

    // Check whether a submitted FARP exists для этого JHA (или job+date fallback)
    useEffect(() => {
        if (!farpRequired) { setFarpExistingPlan(null); return; }
        if (!farpJhaLocalId && (!farpJobCode || !farpDate)) {
            setFarpExistingPlan(null); return;
        }
        let cancelled = false;
        (async () => {
            const criteria = farpJhaLocalId
                ? { jhaLocalId: farpJhaLocalId }
                : { jobCode: farpJobCode, date: farpDate };
            const { exists, plan } = await fallArrestRescuePlanService.checkExisting(criteria);
            if (cancelled) return;
            setFarpExistingPlan(exists ? plan : null);
        })();
        return () => { cancelled = true; };
    }, [farpRequired, farpJhaLocalId, farpJobCode, farpDate]);

    const openFarpFormFromStep4 = () => {
        const params = new URLSearchParams();
        if (farpJobCode)    params.set('jobCode', farpJobCode);
        if (farpDate)       params.set('planDate', farpDate);
        if (farpJhaLocalId) params.set('jhaLocalId', farpJhaLocalId);
        // Возвращаемся на Step4 (этот шаг)
        const returnTo = jhaData?.id
            ? `/jha/step-4?id=${encodeURIComponent(jhaData.id)}`
            : '/jha';
        params.set('returnTo', returnTo);
        navigate(`/safety/forms/fall-arrest-rescue-plan/form?${params.toString()}`);
    };

    useEffect(() => {
        if (!jha?.id) {
            navigate('/jha');
            return;
        }
        loadData();
    }, [jha?.id]);

    // Countdown timer for email cooldown
    useEffect(() => {
        if (emailCooldown > 0) {
            cooldownTimerRef.current = setTimeout(() => {
                setEmailCooldown(prev => prev - 1);
            }, 1000);
        }
        return () => {
            if (cooldownTimerRef.current) {
                clearTimeout(cooldownTimerRef.current);
            }
        };
    }, [emailCooldown]);

    async function loadData() {
        setLoading(true);
        const db = await initializeDB();
        const tx = db.transaction(JHA_STORE, 'readonly');
        const store = tx.objectStore(JHA_STORE);
        const existing = await store.get(jha.id);
        
        if (existing) {
            const initialStatus = existing.stepThree?.initialSubmission?.status;
            // Allow both 'sent' and 'pending' to access step 4
            if (!viewMode && initialStatus !== 'sent' && initialStatus !== 'pending') {
                navigate('/jha/step-3', { state: { jha: existing, isNew: false, viewMode } });
                setLoading(false);
                return;
            }
            if (!viewMode && !(existing.stepFour?.employeesSelected?.length)) {
                navigate('/jha/step-4', { state: { jha: existing, isNew: false, viewMode } });
                setLoading(false);
                return;
            }
            const withMeta = {
                ...existing,
                meta: { ...(existing.meta || {}), lastStep: Math.max(existing.meta?.lastStep || 1, 5) }
            };
            const metaChanged = withMeta.meta.lastStep !== existing.meta?.lastStep;
            if (metaChanged) {
                const writeTx = db.transaction(JHA_STORE, 'readwrite');
                const writeStore = writeTx.objectStore(JHA_STORE);
                await writeStore.put(withMeta);
                await writeTx.done;
            }
            setJhaData(withMeta);
            validateForm(withMeta);
            // Load step 4 data if exists
            if (withMeta.stepFive) {
                setChecklist(withMeta.stepFive.checklist || {});
                setChecklistNotes(withMeta.stepFive.checklistNotes || {});
                setComments(withMeta.stepFive.comments || '');
                setSupervisorSignature(withMeta.stepFive.supervisorSignature || '');
                setSafetyEmployee(withMeta.stepFive.safetyEmployee || null);
                setSafetySignature(withMeta.stepFive.safetySignature || '');
            }
        } else {
            navigate('/jha');
        }
        setLoading(false);
    }

    // Check if JHA is submitted or in view mode
    const isSubmitted = jhaData && (jhaData.status === 'finish' || jhaData.status === 'completed' || jhaData.syncStatus === 'synced');
    const isReadOnly = isSubmitted || viewMode;

    async function saveData() {
        if (!jhaData || isReadOnly) return;

        const updated = {
            ...jhaData,
            stepFive: {
                checklist,
                checklistNotes,
                comments,
                supervisorSignature,
                safetyEmployee,
                safetySignature,
                completedAt: new Date().toISOString()
            },
            meta: {
                ...(jhaData?.meta || {}),
                lastStep: Math.max(jhaData?.meta?.lastStep || 1, 5)
            }
        };

        const db = await initializeDB();
        const tx = db.transaction(JHA_STORE, 'readwrite');
        const store = tx.objectStore(JHA_STORE);
        await store.put(updated);
        await tx.done;

        setJhaData(updated);
        validateForm(updated);

        // Dispatch event
        window.dispatchEvent(new Event('jhaUpdated'));

        return updated;
    }

    function validateForm(data) {
        const newValidation = {
            general: { valid: false, message: '' },
            activities: { valid: false, message: '' },
            docs: { valid: false, message: '' },
            crew: { valid: false, message: '' },
            review: { valid: false, message: '' }
        };

        // Step 1: General Info + Evaluation
        const stepOne = data.stepOne || {};
        const mode = stepOne.mode || MODE_JOB;
        const generalIssues = [];

        if (mode === MODE_JOB && !stepOne.jobNumber) {
            generalIssues.push('Job not selected');
        }
        if (isLocationMode(mode) && !(stepOne.locationName || '').trim()) {
            generalIssues.push('Location not selected');
        }
        if (!stepOne.date) generalIssues.push('Date missing');
        if (!stepOne.jobSiteSupervisor) generalIssues.push('Supervisor missing');

        // Check evaluation - must have at least one item and all items must be selected
        const evaluation = stepOne.evaluationOfWork || [];
        if (evaluation.length > 0 && !evaluation.every(item => item.selected)) {
            generalIssues.push('Evaluation incomplete');
        }

        if (generalIssues.length === 0) {
            newValidation.general.valid = true;
            newValidation.general.message = 'General information complete';
        } else {
            newValidation.general.message = generalIssues[0];
        }

        // Step 2: Documents
        const stepTwo = data.stepTwo || {};
        const allDocs = [
            ...(stepTwo.docsPermits || []),
            ...(stepTwo.customDocsPermits || [])
        ];
        const incompleteDocs = allDocs.filter(doc => !doc.required);
        const requiredDocsWithoutFiles = allDocs.filter(doc => {
            if (doc.required !== 'Yes Req') return false;
            // CSEP is valid if it has linkedCsepUuid
            if (doc.linkedCsepUuid) return false;
            // LTT is valid if it has linkedLttUuid
            if (doc.linkedLttUuid) return false;
            // FARP is valid if it has linkedFarpUuid (a completed Fall Arrest Rescue Plan)
            if (doc.linkedFarpUuid) return false;
            // Other docs need attachments
            return !doc.attachments || doc.attachments.length === 0;
        });

        if (allDocs.length && incompleteDocs.length === 0 && requiredDocsWithoutFiles.length === 0) {
            newValidation.docs.valid = true;
            newValidation.docs.message = 'All documents reviewed and uploaded';
        } else if (!allDocs.length) {
            newValidation.docs.valid = true;
            newValidation.docs.message = 'No documents available';
        } else if (requiredDocsWithoutFiles.length > 0) {
            newValidation.docs.message = `${requiredDocsWithoutFiles.length} required document(s) missing files`;
        } else {
            newValidation.docs.message = `${incompleteDocs.length} document(s) not marked`;
        }

        // Step 3: Work Activities + Energy
        const stepThree = data.stepThree || {};
        const energyRows = (Array.isArray(stepThree.energyActivityRows) && stepThree.energyActivityRows.length)
            ? stepThree.energyActivityRows
            : (stepThree.energyExposures || []);
        const hasAnySelections = energyRows.some(row => getRowSelections(row).length);
        const incompleteRows = energyRows.filter(row => {
            const selections = getRowSelections(row);
            if (!selections.length) {
                return false;
            }
            if (!row.workActivity) {
                return true;
            }
            return selections.some(selection => !selection.energyId || !(selection.hazards && selection.hazards.length));
        });
        const hasActivityDetail = hasAnySelections && incompleteRows.length === 0;
        const initialStatus = stepThree.initialSubmission?.status;
        const initialSent = initialStatus === 'sent' || initialStatus === 'pending';
        if (initialSent && hasActivityDetail) {
            newValidation.activities.valid = true;
            newValidation.activities.message = initialStatus === 'pending' ? 'Initial JHA pending sync' : 'Initial JHA sent';
        } else if (!initialSent) {
            newValidation.activities.message = 'Initial JHA not sent';
        } else if (!hasAnySelections) {
            newValidation.activities.message = 'Add at least one energy exposure';
        } else {
            newValidation.activities.message = 'Complete hazards for all energy selections';
        }

        // Step 4: Crew Selection
        const crewCount = data.stepFour?.employeesSelected?.length || 0;
        const acknowledgments = data.stepFour?.employeeAcknowledgments || [];
        const signedCount = acknowledgments.filter(ack => ack.signature && ack.signedAt).length;

        if (crewCount > 0 && signedCount === crewCount) {
            newValidation.crew.valid = true;
            newValidation.crew.message = `${signedCount}/${crewCount} crew members signed`;
        } else if (crewCount > 0) {
            newValidation.crew.message = `${signedCount}/${crewCount} crew members signed (all must sign)`;
        } else {
            newValidation.crew.message = 'Select at least one crew member';
        }

        // Step 5: Final Review (checklist and signature)
        const stepFive = data.stepFive || {};
        const stepFiveChecklist = stepFive.checklist || {};
        const stepFiveSupervisorSignature = stepFive.supervisorSignature || '';

        const allChecklistDone = checklistItems.every(item => stepFiveChecklist[item.id]);
        const hasSupervisorSignature = Boolean(stepFiveSupervisorSignature);

        if (allChecklistDone && hasSupervisorSignature) {
            newValidation.review.valid = true;
            newValidation.review.message = 'Checklist complete and signed';
        } else if (!allChecklistDone && !hasSupervisorSignature) {
            newValidation.review.message = 'Complete checklist and add signature';
        } else if (!allChecklistDone) {
            const completed = checklistItems.filter(item => stepFiveChecklist[item.id]).length;
            newValidation.review.message = `${completed}/${checklistItems.length} checklist items done`;
        } else {
            newValidation.review.message = 'Supervisor signature required';
        }

        setValidation(newValidation);
    }

    function handleSectionToggle(section) {
        setExpandedSections(prev => ({
            ...prev,
            [section]: !prev[section]
        }));
    }

    function toggleDictation() {
        if (isListening) {
            stopDictation();
        } else {
            startDictation({
                onResult: async (transcript) => {
                    const needsSpace = comments && !comments.endsWith(' ');
                    const newText = comments + (needsSpace ? ' ' : '') + transcript + ' ';
                    setComments(newText);
                    // Save immediately with new comments
                    const existingStepFive = jhaData?.stepFive || {};
                    const updated = {
                        ...jhaData,
                        stepFive: {
                            ...existingStepFive,
                            comments: newText,
                            completedAt: new Date().toISOString()
                        },
                        meta: {
                            ...(jhaData?.meta || {}),
                            lastStep: Math.max(jhaData?.meta?.lastStep || 1, 5)
                        }
                    };
                    const db = await initializeDB();
                    const tx = db.transaction(JHA_STORE, 'readwrite');
                    const store = tx.objectStore(JHA_STORE);
                    await store.put(updated);
                    await tx.done;
                    setJhaData(updated);
                    window.dispatchEvent(new Event('jhaUpdated'));
                }
            });
        }
    }

    async function handleChecklistChange(itemId, value) {
        if (isReadOnly) return;
        const newChecklist = { ...checklist };
        newChecklist[itemId] = value;
        setChecklist(newChecklist);

        // Clear note if changing to yes
        let newNotes = { ...checklistNotes };
        if (value === 'yes' && checklistNotes[itemId]) {
            delete newNotes[itemId];
            setChecklistNotes(newNotes);
        }

        // Save immediately with updated state
        const updated = {
            ...jhaData,
            stepFive: {
                checklist: newChecklist,
                checklistNotes: newNotes,
                comments,
                supervisorSignature,
                safetyEmployee,
                safetySignature,
                completedAt: new Date().toISOString()
            },
            meta: {
                ...(jhaData?.meta || {}),
                lastStep: Math.max(jhaData?.meta?.lastStep || 1, 5)
            }
        };

        const db = await initializeDB();
        const tx = db.transaction(JHA_STORE, 'readwrite');
        const store = tx.objectStore(JHA_STORE);
        await store.put(updated);
        await tx.done;

        setJhaData(updated);
        validateForm(updated);
        window.dispatchEvent(new Event('jhaUpdated'));
    }

    async function handleChecklistNoteChange(itemId, note) {
        if (isReadOnly) return;
        const newNotes = { ...checklistNotes };
        if (note.trim()) {
            newNotes[itemId] = note;
        } else {
            delete newNotes[itemId];
        }
        setChecklistNotes(newNotes);

        // Save immediately with updated state
        const updated = {
            ...jhaData,
            stepFive: {
                checklist,
                checklistNotes: newNotes,
                comments,
                supervisorSignature,
                safetyEmployee,
                safetySignature,
                completedAt: new Date().toISOString()
            },
            meta: {
                ...(jhaData?.meta || {}),
                lastStep: Math.max(jhaData?.meta?.lastStep || 1, 5)
            }
        };

        const db = await initializeDB();
        const tx = db.transaction(JHA_STORE, 'readwrite');
        const store = tx.objectStore(JHA_STORE);
        await store.put(updated);
        await tx.done;

        setJhaData(updated);
        validateForm(updated);
        window.dispatchEvent(new Event('jhaUpdated'));
    }

    async function handlePrev() {
        if (!jhaData) return;
        const saved = await saveData();
        navigate('/jha/step-4', { state: { jha: saved || jhaData, isNew: false, viewMode } });
    }

    async function handleSendEmail() {
        if (!jhaData?.id || sendingEmail || emailCooldown > 0) return;

        setSendingEmail(true);
        try {
            // Get crew members from step 4
            const selectedEmployees = jhaData?.stepFour?.employeesSelected || [];
            const acknowledgments = jhaData?.stepFour?.employeeAcknowledgments || [];

            // Prepare crew members payload
            const crewPayload = selectedEmployees.map(emp => {
                const ack = acknowledgments.find(a =>
                    a.employeeName === emp.FullName && a._type === emp._type
                );
                return {
                    fullName: emp.FullName,
                    signature: ack?.signature || null,
                    _type: emp._type
                };
            });

            const payload = {
                crewMembers: crewPayload
            };

            // Add safety department signature if it exists
            if (safetyEmployee) {
                payload.safetyEmployee = safetyEmployee;
            }
            if (safetySignature) {
                payload.safetySignature = safetySignature;
            }

            // Add post-shift checklist data (current state, not yet saved to DB)
            payload.postShiftChecklist = {
                checklist: checklist,
                checklistNotes: checklistNotes,
                comments: comments,
                supervisorSignature: supervisorSignature
            };

            // Add energy activity rows with hazards (includes custom hazards/actions)
            if (jhaData?.stepThree) {
                const energyRows = jhaData.stepThree.energyActivityRows || jhaData.stepThree.energyExposures || [];
                payload.energyActivityRows = energyRows;
            }

            // Add documents with attachments or linked CSEP from stepTwo
            if (jhaData?.stepTwo) {
                const allDocs = [
                    ...(jhaData.stepTwo.docsPermits || []),
                    ...(jhaData.stepTwo.customDocsPermits || [])
                ];

                // Filter documents that have attachments OR a linked permit/plan (CSEP/LTT/FARP).
                // linkedFarpUuid must be included so the backend attaches the Fall Arrest
                // Rescue Plan PDF to the email (backend handles it at sendEmailWithCrew).
                const docsToSend = allDocs
                    .filter(doc => (doc.attachments && doc.attachments.length > 0) || doc.linkedCsepUuid || doc.linkedLttUuid || doc.linkedFarpUuid)
                    .map(doc => ({
                        value: doc.value,
                        required: doc.required,
                        attachments: doc.attachments || [],
                        linkedCsepUuid: doc.linkedCsepUuid || null,
                        linkedCsepData: doc.linkedCsepData || null,
                        linkedLttUuid: doc.linkedLttUuid || null,
                        linkedLttData: doc.linkedLttData || null,
                        linkedFarpUuid: doc.linkedFarpUuid || null,
                        linkedFarpData: doc.linkedFarpData || null
                    }));

                if (docsToSend.length > 0) {
                    payload.documents = docsToSend;
                }
            }

            const response = await axiosInstance.post(`/jha/local/${jhaData.id}/send-email-with-crew`, payload);

            if (response.data.success) {
                await Swal.fire({
                    icon: 'success',
                    title: 'Email Sent',
                    html: `
                        <div style="text-align: left;">
                            <p>JHA has been sent successfully to:</p>
                            <p><strong>${response.data.sentTo}</strong></p>
                            <p style="color: #666; font-size: 0.9em;">Sent at: ${response.data.sentAt}</p>
                        </div>
                    `,
                    confirmButtonText: 'OK'
                });

                // Start 30 second cooldown
                setEmailCooldown(30);
            }
        } catch (error) {
            console.error('Failed to send email:', error);
            const errorMessage = error?.response?.data?.error || error?.response?.data?.message || 'Failed to send email';

            await Swal.fire({
                icon: 'error',
                title: 'Email Failed',
                text: errorMessage,
                confirmButtonText: 'OK'
            });
        } finally {
            setSendingEmail(false);
        }
    }

    async function handleSubmit() {
        // Validate checklist
        const allChecked = checklistItems.every(item => checklist[item.id]);
        if (!allChecked) {
            alert('Please complete all checklist items before submitting');
            return;
        }

        // Validate signature
        if (!supervisorSignature) {
            alert('Supervisor signature is required before submitting');
            return;
        }

        // ─── Pre-flight: обязательные по триггерам документы без файла/формы ──────
        // Авторитетная проверка, совпадающая с бэкендом (findMissingDocuments):
        // документ считается выполненным, если есть фото-вложение ИЛИ привязан
        // CSEP/LTT/FARP ИЛИ подана FARP-форма. Работает ОФЛАЙН (локальный IDB-
        // оценщик), поэтому блокирует ДО пометки FINISH и до сетевого вызова —
        // юзер сразу видит ПОЧЕМУ (какой триггер) и ЧТО сделать (фото / форма).
        try {
            const missing = await findMissingDocumentsLocal(jhaData);
            if (missing.length > 0) {
                setSubmitDialog(false);
                await showMissingDocsDialog(missing);
                return;
            }
        } catch (e) {
            console.warn('[JhaStepFour] pre-flight trigger check failed (continuing):', e);
        }

        // ─── Soft warning: FARP требуется, но НЕ выполнен (ни фото, ни привязки,
        // ни поданной формы). Если приложено фото бланка — farpSatisfied true и
        // модалка не показывается. Не блокирует submit, лишь напоминает. ──────────
        if (farpRequired && !farpSatisfied) {
            // Закрываем MUI submit-dialog ДО Swal — иначе они стэкаются друг на
            // друге (Submit JHA? остаётся виден поверх warning). MUI Dialog
            // имеет z-index 1300, Swal по умолчанию 1060 → Swal перекрывается.
            // Заодно повышаем Swal-контейнер до 13000 как страховку.
            setSubmitDialog(false);
            const triggers = triggeredActivities.join(', ');
            const result = await Swal.fire({
                title: 'Fall Arrest Rescue Plan not found',
                html: `Selected work activities require a rescue plan:<br><br>` +
                      `<em>${triggers}</em><br><br>` +
                      `No submitted Fall Arrest Rescue Plan exists for this job (` +
                      `<strong>${farpJobCode || 'no job'}</strong>) on <strong>${farpDate}</strong>.<br><br>` +
                      `You can submit the JHA now and file the rescue plan later, or open the rescue plan form first.`,
                icon: 'warning',
                showDenyButton: true,
                showCancelButton: true,
                confirmButtonText: 'Open Rescue Plan',
                confirmButtonColor: '#d32f2f',
                denyButtonText: 'Submit anyway',
                denyButtonColor: '#ff9800',
                cancelButtonText: 'Cancel',
                didOpen: () => {
                    document.querySelectorAll('.swal2-container').forEach((c) => {
                        c.style.zIndex = '13000';
                    });
                },
            });
            if (result.isConfirmed) {
                openFarpFormFromStep4();
                return;
            }
            if (!result.isDenied) return; // Cancel — submitDialog уже закрыт, выходим
            // isDenied → продолжаем submit + reminder toast
            Swal.fire({
                ...TOAST_DEFAULTS,
                icon: 'warning',
                title: 'Reminder: file the Fall Arrest Rescue Plan ASAP',
                timer: 4500,
                timerProgressBar: true,
            });
        }

        setSubmitting(true);
        
        try {
            // Update status and save
            const updated = {
                ...jhaData,
                stepFive: {
                    checklist,
                    checklistNotes,
                    comments,
                    supervisorSignature,
                    completedAt: new Date().toISOString()
                },
                status: STATUS_FINISH,
                submittedAt: new Date().toISOString(),
                meta: {
                    ...(jhaData?.meta || {}),
                    lastStep: Math.max(jhaData?.meta?.lastStep || 1, 5)
                }
            };

            const db = await initializeDB();
            const tx = db.transaction(JHA_STORE, 'readwrite');
            const store = tx.objectStore(JHA_STORE);
            await store.put(updated);
            
            // Try to sync to server
            try {
                const apiPayload = buildJhaPayload(updated) || {};
                apiPayload.submittedAt = updated.submittedAt || apiPayload.submittedAt;

                const response = await axiosInstance.post('/jha/submit', apiPayload);
                // Server returned success, update local status
                updated.syncStatus = 'synced';
                updated.syncedAt = new Date().toISOString();
                
                // Create new transaction for update since previous one is done
                const updateTx = db.transaction(JHA_STORE, 'readwrite');
                const updateStore = updateTx.objectStore(JHA_STORE);
                await updateStore.put(updated);
                await updateTx.done;
            } catch (error) {
                const httpStatus = error?.response?.status;
                const data = error?.response?.data || {};
                const missing = Array.isArray(data.missing) ? data.missing : [];

                // 422 = ЖЁСТКИЙ отказ валидации (например, сработал триггер и нужен
                // документ). Повторная отправка того же payload всегда упрётся в ту же
                // 422 — поэтому НЕ помечаем pending/submitted, а откатываем статус назад
                // (в process), чтобы JHA остался редактируемым, и показываем юзеру ПОЧЕМУ
                // и ЧТО сделать. Без этого 422 раньше молча проглатывалась и JHA "висел"
                // как отправленный, хотя сервер его отклонил.
                if (httpStatus === 422) {
                    // Добавляем недостающие (по мнению бэкенда) документы в список,
                    // чтобы юзеру было куда приложить файл/форму, даже если локальный
                    // оценщик их не сматчил.
                    const { stepTwo: injectedStepTwo } = injectMissingDocRows(updated.stepTwo, missing);
                    // status=process сам по себе выводит запись из фоновой очереди
                    // синка (она берёт только finish/completed). syncStatus очищаем
                    // (null), чтобы в списке не висел ложный бейдж "Pending sync".
                    const reverted = {
                        ...updated,
                        stepTwo: injectedStepTwo,
                        status: STATUS_PROCESS,
                        submittedAt: null,
                        syncStatus: null,
                        stepFive: { ...(updated.stepFive || {}), completedAt: null },
                    };
                    const revertTx = db.transaction(JHA_STORE, 'readwrite');
                    await revertTx.objectStore(JHA_STORE).put(reverted);
                    await revertTx.done;
                    setJhaData(reverted);
                    window.dispatchEvent(new Event('jhaUpdated'));

                    setSubmitDialog(false);
                    if (missing.length > 0) {
                        await showMissingDocsDialog(missing);
                    } else {
                        await Swal.fire({
                            icon: 'warning',
                            title: 'Cannot submit yet',
                            text: data.message || data.error || 'Some required information is missing. Please review the form and try again.',
                            confirmButtonText: 'OK',
                            confirmButtonColor: '#f59e0b',
                            didOpen: () => {
                                document.querySelectorAll('.swal2-container').forEach((c) => { c.style.zIndex = '13000'; });
                            },
                        });
                    }
                    return; // НЕ помечаем отправленным — остаёмся для исправления
                }

                // Прочие ошибки (нет сети / 5xx) — это нормально для offline-first:
                // сохраняем как pending, синк повторится автоматически позже.
                console.error('Sync failed, will retry later:', error);
                updated.syncStatus = 'pending';

                // Create new transaction for update
                const updateTx = db.transaction(JHA_STORE, 'readwrite');
                const updateStore = updateTx.objectStore(JHA_STORE);
                await updateStore.put(updated);
                await updateTx.done;
            }

            setSubmitDialog(false);
            navigate('/jha', { state: { submitted: true } });
        } catch (error) {
            console.error('Submit failed:', error);
            alert('Failed to submit JHA. Please try again.');
        } finally {
            setSubmitting(false);
        }
    }

    // Calculate progress
    const checklistProgress = useMemo(() => {
        const total = checklistItems.length;
        const completed = checklistItems.filter(item => checklist[item.id]).length;
        return {
            percentage: total > 0 ? (completed / total) * 100 : 0,
            completed,
            total
        };
    }, [checklist]);

    const isAllValid = Object.values(validation).every(section => section.valid);
    const canSubmit = isAllValid && checklistProgress.percentage === 100 && supervisorSignature && !isReadOnly;

    const stepOne = jhaData?.stepOne || {};
    const jobTitle = stepOne.jobNumber || stepOne.locationName || 'N/A';
    const jobSubtitle = stepOne.projectName || stepOne.taskLocation || stepOne.jobSiteName || 'No site specified';
    const crewCount = jhaData?.stepFour?.employeesSelected?.length || 0;
    // ВНИМАНИЕ: шаги 2 и 3 поменяны местами (2 = Work Activities, 3 = Documents).
    // Ключи (docs/activities) индексируют состояние `validation` и НЕ меняются,
    // но label + step (для навигации /jha/step-{step}) должны соответствовать
    // новому порядку, иначе клик по строке уводит не на тот шаг.
    const validationLabels = {
        general: { label: 'Step 1: General Information', step: 1 },
        activities: { label: 'Step 2: Work Activities', step: 2 },
        docs: { label: 'Step 3: Documents & Permits', step: 3 },
        crew: { label: 'Step 4: Crew Selection', step: 4 },
        review: { label: 'Step 5: Final Review', step: 5 }
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box
            sx={{
                height: 'calc(100vh - 64px)',
                backgroundColor: '#f5f5f5',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden'
            }}
        >
            {/* Header */}
            <JhaStepHeader
                currentStep={5}
                jhaData={jhaData}
                fromViewer={viewMode}
            />

            {/* Content */}
            <Box sx={{ flex: 1, overflow: 'auto', display: 'flex', justifyContent: 'center', minHeight: 0 }}>
                <Box sx={{ width: '100%', maxWidth: 1200, px: { xs: 1.5, md: 2.5 }, py: { xs: 1.5, md: 2 }, pb: { xs: 12, md: 14 } }}>
                {/* Read-only alert */}
                {isReadOnly && (
                    <Alert severity="info" sx={{ mb: 2 }}>
                        This JHA is in read-only mode. You can navigate between steps but cannot make changes.
                    </Alert>
                )}
                {/* Validation Status */}
                {!isReadOnly && (
                    <Card sx={{ mb: 1 }}>
                        <Box sx={{
                            p: 1.5,
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between'
                        }} onClick={() => handleSectionToggle('validation')}>
                            <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <SecurityIcon />
                                Form Validation
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                {isAllValid ? (
                                    <Chip label="All Valid" color="success" size="small" />
                                ) : (
                                    <Chip label="Incomplete" color="warning" size="small" />
                                )}
                                <IconButton size="small">
                                    {expandedSections.validation ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                </IconButton>
                            </Box>
                        </Box>
                        <Collapse in={expandedSections.validation}>
                            <Divider />
                            <Box sx={{ p: 1.5 }}>
                                <Stack spacing={0.75}>
                                    {Object.entries(validation).map(([step, status]) => {
                                        const stepInfo = validationLabels[step];
                                        const stepRoute = `/jha/step-${stepInfo.step}`;

                                        return (
                                            <Box
                                                key={step}
                                                onClick={() => !isReadOnly && navigate(stepRoute, { state: { jha: jhaData, isNew: false, viewMode } })}
                                                sx={{
                                                    p: 1.5,
                                                    border: '1px solid #e0e0e0',
                                                    borderRadius: 1,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 1.5,
                                                    cursor: isReadOnly ? 'default' : 'pointer',
                                                    '&:hover': isReadOnly ? {} : { backgroundColor: '#f5f5f5' }
                                                }}
                                            >
                                                {status.valid ? (
                                                    <CheckCircleIcon color="success" sx={{ fontSize: 20 }} />
                                                ) : (
                                                    <CancelIcon color="error" sx={{ fontSize: 20 }} />
                                                )}
                                                <Box sx={{ flex: 1 }}>
                                                    <Typography variant="body2" fontWeight={600}>
                                                        {stepInfo.label}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        {status.message}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        );
                                    })}
                                </Stack>
                            </Box>
                        </Collapse>
                    </Card>
                )}

                {/* Post-Shift Checklist */}
                <Card sx={{ mb: 1 }}>
                    <Box sx={{
                        p: 1.5,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between'
                    }} onClick={() => handleSectionToggle('checklist')}>
                        <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <CheckCircleIcon />
                            Post-Shift Checklist
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {!isReadOnly && (
                                <>
                                    <LinearProgress 
                                        variant="determinate" 
                                        value={checklistProgress.percentage} 
                                        sx={{ width: 100, mr: 1 }}
                                    />
                                    <Typography variant="caption">
                                        {checklistProgress.completed}/{checklistProgress.total}
                                    </Typography>
                                </>
                            )}
                            <IconButton size="small">
                                {expandedSections.checklist ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                            </IconButton>
                        </Box>
                    </Box>
                    <Collapse in={expandedSections.checklist}>
                        <Divider />
                        <Box sx={{ p: 1.5 }}>
                            <Grid container spacing={0.5}>
                                {checklistItems.map((item) => {
                                    const ItemIcon = item.icon;
                                    const selectedValue = checklist[item.id] || null;

                                    return (
                                        <Grid item xs={12} sm={6} key={item.id}>
                                            <Paper
                                                elevation={0}
                                                sx={{
                                                    p: 0.65,
                                                    border: '1px solid',
                                                    borderColor: '#e0e0e0',
                                                    backgroundColor: '#fafafa',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 0.65,
                                                    '&:hover': {
                                                        backgroundColor: '#f5f5f5'
                                                    }
                                                }}
                                            >
                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        fontSize: '0.725rem',
                                                        lineHeight: 1.3,
                                                        flex: 1,
                                                        minWidth: 0
                                                    }}
                                                >
                                                    {item.label}
                                                </Typography>
                                                {!isReadOnly ? (
                                                    <ToggleButtonGroup
                                                        value={selectedValue}
                                                        exclusive
                                                        onChange={(e, newValue) => {
                                                            if (newValue !== null) {
                                                                handleChecklistChange(item.id, newValue);
                                                            }
                                                        }}
                                                        size="small"
                                                        sx={{
                                                            flexShrink: 0,
                                                            '& .MuiToggleButton-root': {
                                                                px: 2,
                                                                py: 0.5,
                                                                fontSize: '0.75rem',
                                                                textTransform: 'none',
                                                                border: '1px solid #bdbdbd',
                                                                minHeight: 32,
                                                                '&.Mui-selected': {
                                                                    fontWeight: 600,
                                                                    border: '1px solid',
                                                                    '&:hover': {
                                                                        opacity: 0.9
                                                                    }
                                                                },
                                                                '&:not(.Mui-selected)': {
                                                                    color: '#666'
                                                                }
                                                            }
                                                        }}
                                                    >
                                                        <ToggleButton
                                                            value="yes"
                                                            sx={{
                                                                '&.Mui-selected': {
                                                                    backgroundColor: '#4caf50',
                                                                    color: '#fff',
                                                                    borderColor: '#4caf50 !important',
                                                                    '&:hover': {
                                                                        backgroundColor: '#45a049'
                                                                    }
                                                                }
                                                            }}
                                                        >
                                                            YES
                                                        </ToggleButton>
                                                        <ToggleButton
                                                            value="no"
                                                            sx={{
                                                                '&.Mui-selected': {
                                                                    backgroundColor: '#f44336',
                                                                    color: '#fff',
                                                                    borderColor: '#f44336 !important',
                                                                    '&:hover': {
                                                                        backgroundColor: '#e53935'
                                                                    }
                                                                }
                                                            }}
                                                        >
                                                            NO
                                                        </ToggleButton>
                                                    </ToggleButtonGroup>
                                                ) : (
                                                    <Chip
                                                        label={selectedValue === 'yes' ? 'YES' : selectedValue === 'no' ? 'NO' : 'N/A'}
                                                        size="small"
                                                        color={selectedValue === 'yes' ? 'success' : selectedValue === 'no' ? 'error' : 'default'}
                                                        sx={{ fontSize: '0.68rem' }}
                                                    />
                                                )}
                                            </Paper>
                                        </Grid>
                                    );
                                })}
                            </Grid>

                            {/* Comments Section */}
                            <Box sx={{ mt: 1.5 }}>
                                    <Typography variant="body2" fontWeight={600} sx={{ mb: 0.75, display: 'flex', alignItems: 'center', gap: 0.5, fontSize: '0.813rem' }}>
                                        <CommentIcon sx={{ fontSize: 16 }} />
                                        Comments
                                    </Typography>
                                    <TextField
                                        multiline
                                        rows={2}
                                        fullWidth
                                        size="small"
                                        placeholder="Enter any additional comments..."
                                        value={comments}
                                        onChange={(e) => {
                                            if (!isReadOnly) {
                                                setComments(e.target.value);
                                            }
                                        }}
                                        onBlur={async (e) => {
                                            if (isReadOnly) return;
                                            // Save on blur with current value
                                            const existingStepFive = jhaData?.stepFive || {};
                                            const updated = {
                                                ...jhaData,
                                                stepFive: {
                                                    ...existingStepFive,
                                                    comments: e.target.value,
                                                    completedAt: new Date().toISOString()
                                                },
                                                meta: {
                                                    ...(jhaData?.meta || {}),
                                                    lastStep: Math.max(jhaData?.meta?.lastStep || 1, 5)
                                                }
                                            };
                                            const db = await initializeDB();
                                            const tx = db.transaction(JHA_STORE, 'readwrite');
                                            const store = tx.objectStore(JHA_STORE);
                                            await store.put(updated);
                                            await tx.done;
                                            setJhaData(updated);
                                            window.dispatchEvent(new Event('jhaUpdated'));
                                        }}
                                        variant="outlined"
                                        disabled={isReadOnly}
                                        InputProps={{
                                            endAdornment: speechSupported && !isReadOnly && (
                                                <InputAdornment position="end" sx={{ alignSelf: 'flex-start', mt: 1 }}>
                                                    <IconButton
                                                        onClick={toggleDictation}
                                                        edge="end"
                                                        color={isListening ? 'error' : 'default'}
                                                        size="small"
                                                    >
                                                        {isListening ? <MicIcon /> : <MicOffIcon />}
                                                    </IconButton>
                                                </InputAdornment>
                                            )
                                        }}
                                    />
                                </Box>

                                {/* Supervisor Signature */}
                                {!isReadOnly && (
                                    <Box sx={{ mt: 1.5 }}>
                                        <Typography variant="body2" fontWeight="600" sx={{ mb: 1, fontSize: '0.875rem' }}>
                                            <DrawIcon sx={{ fontSize: 16, verticalAlign: 'middle', mr: 0.5 }} />
                                            Post-Shift Supervisor Signature
                                            <Typography component="span" color="error"> *</Typography>
                                        </Typography>

                                        <Paper
                                            variant="outlined"
                                            sx={{
                                                borderRadius: 1,
                                                overflow: 'hidden',
                                                border: supervisorSignature ? '1px solid #4caf50' : '1px solid #e0e0e0'
                                            }}
                                        >
                                            {/* Supervisor Info Header */}
                                            <Box sx={{
                                                backgroundColor: supervisorSignature ? '#e8f5e8' : '#f5f5f5',
                                                borderBottom: '1px solid #e0e0e0',
                                                p: 1,
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1
                                            }}>
                                                <Box sx={{
                                                    backgroundColor: supervisorSignature ? '#4caf50' : '#9e9e9e',
                                                    color: 'white',
                                                    borderRadius: '50%',
                                                    width: 24,
                                                    height: 24,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    flexShrink: 0
                                                }}>
                                                    {supervisorSignature ? (
                                                        <CheckCircleIcon sx={{ fontSize: 16 }} />
                                                    ) : (
                                                        <RadioButtonUnchecked sx={{ fontSize: 16 }} />
                                                    )}
                                                </Box>
                                                <Box sx={{ flexGrow: 1 }}>
                                                    <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.813rem' }}>
                                                        {jhaData?.stepOne?.personCompleting?.FullName || jhaData?.stepOne?.personCompleting || 'Supervisor'}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                                                        Post-Shift Supervisor
                                                    </Typography>
                                                </Box>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    {supervisorSignature ? (
                                                        <Chip 
                                                            icon={<CheckCircleIcon />}
                                                            label="Signed" 
                                                            color="success" 
                                                            size="small"
                                                        />
                                                    ) : (
                                                        <Chip 
                                                            icon={<RadioButtonUnchecked />}
                                                            label="Pending" 
                                                            color="default" 
                                                            size="small"
                                                            variant="outlined"
                                                        />
                                                    )}
                                                </Box>
                                            </Box>

                                            {/* Signature Content */}
                                            <Box sx={{ p: 1.5 }}>
                                                {supervisorSignature ? (
                                                    <Box>
                                                        <Typography variant="caption" color="text.secondary" sx={{ mb: 0.75, display: 'block', fontSize: '0.7rem' }}>
                                                            Digital Signature:
                                                        </Typography>
                                                        <Box sx={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: 1,
                                                            flexWrap: 'wrap'
                                                        }}>
                                                            <Paper
                                                                sx={{
                                                                    border: '1px solid #4caf50',
                                                                    borderRadius: 1,
                                                                    p: 0.5,
                                                                    backgroundColor: '#f1f8e9',
                                                                    flexGrow: 1,
                                                                    minWidth: 150
                                                                }}
                                                            >
                                                                <Box
                                                                    component="img"
                                                                    src={supervisorSignature}
                                                                    alt="Supervisor Signature"
                                                                    sx={{
                                                                        maxWidth: '100%',
                                                                        maxHeight: 50,
                                                                        borderRadius: 0.5,
                                                                        backgroundColor: '#fff'
                                                                    }}
                                                                />
                                                            </Paper>
                                                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                                                                <Button
                                                                    variant="outlined"
                                                                    size="small"
                                                                    onClick={() => setSignatureModalOpen(true)}
                                                                    sx={{ minWidth: 80, fontSize: '0.75rem', py: 0.5 }}
                                                                >
                                                                    Update
                                                                </Button>
                                                                <Button
                                                                    variant="outlined"
                                                                    color="error"
                                                                    size="small"
                                                                    onClick={async () => {
                                                                        setSupervisorSignature('');
                                                                        // Save immediately with cleared signature
                                                                        const existingStepFive = jhaData?.stepFive || {};
                                                                        const updated = {
                                                                            ...jhaData,
                                                                            stepFive: {
                                                                                ...existingStepFive,
                                                                                supervisorSignature: '',
                                                                                completedAt: new Date().toISOString()
                                                                            },
                                                                            meta: {
                                                                                ...(jhaData?.meta || {}),
                                                                                lastStep: Math.max(jhaData?.meta?.lastStep || 1, 5)
                                                                            }
                                                                        };
                                                                        const db = await initializeDB();
                                                                        const tx = db.transaction(JHA_STORE, 'readwrite');
                                                                        const store = tx.objectStore(JHA_STORE);
                                                                        await store.put(updated);
                                                                        await tx.done;
                                                                        setJhaData(updated);
                                                                        validateForm(updated);
                                                                        window.dispatchEvent(new Event('jhaUpdated'));
                                                                    }}
                                                                    sx={{ minWidth: 80, fontSize: '0.75rem', py: 0.5 }}
                                                                >
                                                                    Remove
                                                                </Button>
                                                            </Box>
                                                        </Box>
                                                    </Box>
                                                ) : (
                                                    <Box sx={{
                                                        textAlign: 'center',
                                                        p: 1.5,
                                                        backgroundColor: '#fafafa',
                                                        borderRadius: 1,
                                                        border: '1px dashed #ddd'
                                                    }}>
                                                        <Box sx={{ mb: 0.75 }}>
                                                            <RadioButtonUnchecked sx={{ fontSize: 32, color: '#ccc' }} />
                                                        </Box>
                                                        <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block', fontSize: '0.75rem' }}>
                                                            Supervisor signature is required to complete the JHA
                                                        </Typography>
                                                        <Button
                                                            variant="contained"
                                                            disabled={checklistProgress.percentage < 100}
                                                            onClick={() => setSignatureModalOpen(true)}
                                                            sx={{ minWidth: 120, fontSize: '0.75rem', py: 0.5 }}
                                                            size="small"
                                                        >
                                                            Add Signature
                                                        </Button>
                                                    </Box>
                                                )}
                                            </Box>
                                        </Paper>
                                    </Box>
                                )}
                                {/* Show signature in read-only mode if exists */}
                                {isReadOnly && supervisorSignature && (
                                    <Box sx={{ mt: 3 }}>
                                        <Typography variant="subtitle1" fontWeight="600" sx={{ mb: 2 }}>
                                            <DrawIcon sx={{ fontSize: 20, verticalAlign: 'middle', mr: 1 }} />
                                            Post-Shift Supervisor Signature
                                        </Typography>
                                        
                                        <Paper
                                            variant="outlined"
                                            sx={{ 
                                                borderRadius: 2,
                                                overflow: 'hidden',
                                                border: '2px solid #4caf50'
                                            }}
                                        >
                                            {/* Supervisor Info Header */}
                                            <Box sx={{ 
                                                backgroundColor: '#e8f5e8',
                                                borderBottom: '1px solid #e0e0e0',
                                                p: 2,
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 2
                                            }}>
                                                <Box sx={{ 
                                                    backgroundColor: '#4caf50',
                                                    color: 'white',
                                                    borderRadius: '50%',
                                                    width: 32,
                                                    height: 32,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    flexShrink: 0
                                                }}>
                                                    <CheckCircleIcon sx={{ fontSize: 20 }} />
                                                </Box>
                                                <Box sx={{ flexGrow: 1 }}>
                                                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                                                        {jhaData?.stepOne?.personCompleting?.FullName || jhaData?.stepOne?.personCompleting || 'Supervisor'}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        Post-Shift Supervisor
                                                    </Typography>
                                                </Box>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <Chip 
                                                        icon={<CheckCircleIcon />}
                                                        label="Signed" 
                                                        color="success" 
                                                        size="small"
                                                    />
                                                </Box>
                                            </Box>

                                            {/* Signature Content */}
                                            <Box sx={{ p: 3 }}>
                                                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                                    Digital Signature:
                                                </Typography>
                                                <Paper
                                                    sx={{
                                                        border: '2px solid #4caf50',
                                                        borderRadius: 2,
                                                        p: 1,
                                                        backgroundColor: '#f1f8e9',
                                                        maxWidth: 400
                                                    }}
                                                >
                                                    <Box
                                                        component="img"
                                                        src={supervisorSignature}
                                                        alt="Supervisor Signature"
                                                        sx={{
                                                            maxWidth: '100%',
                                                            maxHeight: 80,
                                                            borderRadius: 1,
                                                            backgroundColor: '#fff'
                                                        }}
                                                    />
                                                </Paper>
                                            </Box>
                                        </Paper>
                                    </Box>
                                )}

                                {/* Safety Department Plan Review Signature (Optional) */}
                                {!isReadOnly && (
                                    <Box sx={{ mt: 1.5 }}>
                                        <Typography variant="body2" fontWeight="600" sx={{ mb: 1, fontSize: '0.875rem' }}>
                                            <SecurityIcon sx={{ fontSize: 16, verticalAlign: 'middle', mr: 0.5 }} />
                                            Safety Department Plan Review
                                            <Typography component="span" color="text.secondary" sx={{ fontSize: '0.75rem', ml: 0.5 }}>(If Applicable)</Typography>
                                        </Typography>

                                        {!safetyEmployee ? (
                                            <Button
                                                variant="outlined"
                                                size="small"
                                                startIcon={<SecurityIcon />}
                                                onClick={() => setSafetyEmployeeModalOpen(true)}
                                                sx={{ fontSize: '0.75rem' }}
                                            >
                                                Select Safety Employee
                                            </Button>
                                        ) : (
                                            <Paper
                                                variant="outlined"
                                                sx={{
                                                    borderRadius: 1,
                                                    overflow: 'hidden',
                                                    border: safetySignature ? '1px solid #4caf50' : '1px solid #e0e0e0'
                                                }}
                                            >
                                                {/* Safety Employee Info Header */}
                                                <Box sx={{
                                                    backgroundColor: safetySignature ? '#e8f5e8' : '#f5f5f5',
                                                    borderBottom: '1px solid #e0e0e0',
                                                    p: 1,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 1
                                                }}>
                                                    <Box sx={{
                                                        backgroundColor: safetySignature ? '#4caf50' : '#9e9e9e',
                                                        color: 'white',
                                                        borderRadius: '50%',
                                                        width: 24,
                                                        height: 24,
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        flexShrink: 0
                                                    }}>
                                                        {safetySignature ? (
                                                            <CheckCircleIcon sx={{ fontSize: 16 }} />
                                                        ) : (
                                                            <RadioButtonUnchecked sx={{ fontSize: 16 }} />
                                                        )}
                                                    </Box>
                                                    <Box sx={{ flexGrow: 1 }}>
                                                        <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.813rem' }}>
                                                            {safetyEmployee.FullName}
                                                        </Typography>
                                                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                                                            {safetyEmployee.JobClassification || 'Safety Department'}
                                                        </Typography>
                                                    </Box>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        {safetySignature ? (
                                                            <Chip
                                                                icon={<CheckCircleIcon />}
                                                                label="Signed"
                                                                color="success"
                                                                size="small"
                                                                sx={{ fontSize: '0.7rem', height: 22 }}
                                                            />
                                                        ) : (
                                                            <Chip
                                                                label="Pending"
                                                                color="default"
                                                                size="small"
                                                                sx={{ fontSize: '0.7rem', height: 22 }}
                                                            />
                                                        )}
                                                        <IconButton
                                                            size="small"
                                                            onClick={async () => {
                                                                setSafetyEmployee(null);
                                                                setSafetySignature('');
                                                                // Save immediately with cleared safety data
                                                                const existingStepFive = jhaData?.stepFive || {};
                                                                const updated = {
                                                                    ...jhaData,
                                                                    stepFive: {
                                                                        ...existingStepFive,
                                                                        safetyEmployee: null,
                                                                        safetySignature: '',
                                                                        completedAt: new Date().toISOString()
                                                                    },
                                                                    meta: {
                                                                        ...(jhaData?.meta || {}),
                                                                        lastStep: Math.max(jhaData?.meta?.lastStep || 1, 5)
                                                                    }
                                                                };
                                                                const db = await initializeDB();
                                                                const tx = db.transaction(JHA_STORE, 'readwrite');
                                                                const store = tx.objectStore(JHA_STORE);
                                                                await store.put(updated);
                                                                await tx.done;
                                                                setJhaData(updated);
                                                                validateForm(updated);
                                                                window.dispatchEvent(new Event('jhaUpdated'));
                                                            }}
                                                            sx={{ fontSize: '0.7rem' }}
                                                        >
                                                            <CloseIcon fontSize="small" />
                                                        </IconButton>
                                                    </Box>
                                                </Box>

                                                {/* Signature Content */}
                                                <Box sx={{ p: 1.5 }}>
                                                    {safetySignature ? (
                                                        <Box>
                                                            <Typography variant="caption" color="text.secondary" sx={{ mb: 0.75, display: 'block', fontSize: '0.7rem' }}>
                                                                Digital Signature:
                                                            </Typography>
                                                            <Box sx={{
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                gap: 1,
                                                                flexWrap: 'wrap'
                                                            }}>
                                                                <Paper
                                                                    sx={{
                                                                        border: '1px solid #4caf50',
                                                                        borderRadius: 1,
                                                                        p: 0.5,
                                                                        backgroundColor: '#f1f8e9',
                                                                        flexGrow: 1,
                                                                        minWidth: 150
                                                                    }}
                                                                >
                                                                    <Box
                                                                        component="img"
                                                                        src={safetySignature}
                                                                        alt="Safety Department Signature"
                                                                        sx={{
                                                                            maxWidth: '100%',
                                                                            maxHeight: 50,
                                                                            borderRadius: 0.5,
                                                                            backgroundColor: '#fff'
                                                                        }}
                                                                    />
                                                                </Paper>
                                                                <Box sx={{ display: 'flex', gap: 0.5 }}>
                                                                    <Button
                                                                        variant="outlined"
                                                                        size="small"
                                                                        onClick={() => setSafetySignatureModalOpen(true)}
                                                                        sx={{ minWidth: 80, fontSize: '0.75rem', py: 0.5 }}
                                                                    >
                                                                        Update
                                                                    </Button>
                                                                    <Button
                                                                        variant="outlined"
                                                                        color="error"
                                                                        size="small"
                                                                        onClick={async () => {
                                                                            setSafetySignature('');
                                                                            // Save immediately with cleared safety signature
                                                                            const existingStepFive = jhaData?.stepFive || {};
                                                                            const updated = {
                                                                                ...jhaData,
                                                                                stepFive: {
                                                                                    ...existingStepFive,
                                                                                    safetySignature: '',
                                                                                    completedAt: new Date().toISOString()
                                                                                },
                                                                                meta: {
                                                                                    ...(jhaData?.meta || {}),
                                                                                    lastStep: Math.max(jhaData?.meta?.lastStep || 1, 5)
                                                                                }
                                                                            };
                                                                            const db = await initializeDB();
                                                                            const tx = db.transaction(JHA_STORE, 'readwrite');
                                                                            const store = tx.objectStore(JHA_STORE);
                                                                            await store.put(updated);
                                                                            await tx.done;
                                                                            setJhaData(updated);
                                                                            validateForm(updated);
                                                                            window.dispatchEvent(new Event('jhaUpdated'));
                                                                        }}
                                                                        sx={{ minWidth: 80, fontSize: '0.75rem', py: 0.5 }}
                                                                    >
                                                                        Remove
                                                                    </Button>
                                                                </Box>
                                                            </Box>
                                                        </Box>
                                                    ) : (
                                                        <Box sx={{
                                                            textAlign: 'center',
                                                            p: 1.5,
                                                            backgroundColor: '#fafafa',
                                                            borderRadius: 1,
                                                            border: '1px dashed #ddd'
                                                        }}>
                                                            <Box sx={{ mb: 0.75 }}>
                                                                <RadioButtonUnchecked sx={{ fontSize: 32, color: '#ccc' }} />
                                                            </Box>
                                                            <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block', fontSize: '0.75rem' }}>
                                                                Capture signature from selected safety employee
                                                            </Typography>
                                                            <Button
                                                                variant="contained"
                                                                onClick={() => setSafetySignatureModalOpen(true)}
                                                                sx={{ minWidth: 120, fontSize: '0.75rem', py: 0.5 }}
                                                                size="small"
                                                            >
                                                                Add Signature
                                                            </Button>
                                                        </Box>
                                                    )}
                                                </Box>
                                            </Paper>
                                        )}
                                    </Box>
                                )}
                        </Box>
                    </Collapse>
                </Card>
                </Box>
            </Box>

            {/* Bottom Actions */}
            {!isReadOnly && (
                <Box
                    sx={{
                        position: 'sticky',
                        bottom: 0,
                        backgroundColor: 'white',
                        borderTop: '1px solid #e0e0e0',
                        px: { xs: 1.5, md: 2.5 },
                        py: 1.5,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        gap: 2
                    }}
                >
                    <Stack direction="row" spacing={1.5} alignItems="center">
                        <Button
                            variant="outlined"
                            startIcon={<ArrowBackIcon />}
                            onClick={handlePrev}
                        >
                            Back to Crew Selection
                        </Button>

                        {/* Send Email Button */}
                        <Button
                            variant="outlined"
                            color={emailCooldown > 0 ? 'secondary' : 'primary'}
                            startIcon={sendingEmail ? <CircularProgress size={16} /> : <EmailIcon />}
                            onClick={handleSendEmail}
                            disabled={sendingEmail || emailCooldown > 0}
                            sx={{
                                whiteSpace: 'nowrap',
                                position: 'relative',
                                minWidth: emailCooldown > 0 ? 140 : 120
                            }}
                        >
                            {sendingEmail ? 'Sending...' : emailCooldown > 0 ? `Wait ${emailCooldown}s` : 'Send Email'}
                            {emailCooldown > 0 && (
                                <LinearProgress
                                    variant="determinate"
                                    value={((30 - emailCooldown) / 30) * 100}
                                    sx={{
                                        position: 'absolute',
                                        bottom: 0,
                                        left: 0,
                                        right: 0,
                                        height: 3,
                                        borderBottomLeftRadius: 4,
                                        borderBottomRightRadius: 4
                                    }}
                                />
                            )}
                        </Button>
                    </Stack>

                    <Button
                        variant="contained"
                        color="success"
                        disabled={!canSubmit}
                        onClick={() => setSubmitDialog(true)}
                        startIcon={<SendIcon />}
                    >
                        Submit JHA
                    </Button>
                </Box>
            )}

            {/* Signature Modal */}
            <SignatureModal
                open={signatureModalOpen}
                onClose={() => setSignatureModalOpen(false)}
                existingSignature={supervisorSignature}
                employeeName="Supervisor"
                onSave={async (sig) => {
                    setSupervisorSignature(sig);
                    setSignatureModalOpen(false);

                    // Save immediately with new signature (preserve existing stepFive data)
                    const existingStepFive = jhaData?.stepFive || {};
                    const updated = {
                        ...jhaData,
                        stepFive: {
                            ...existingStepFive,
                            checklist,
                            checklistNotes,
                            comments,
                            supervisorSignature: sig,
                            completedAt: new Date().toISOString()
                        },
                        meta: {
                            ...(jhaData?.meta || {}),
                            lastStep: Math.max(jhaData?.meta?.lastStep || 1, 5)
                        }
                    };

                    const db = await initializeDB();
                    const tx = db.transaction(JHA_STORE, 'readwrite');
                    const store = tx.objectStore(JHA_STORE);
                    await store.put(updated);
                    await tx.done;

                    setJhaData(updated);
                    validateForm(updated);
                    window.dispatchEvent(new Event('jhaUpdated'));
                }}
            />

            {/* Safety Employee Selection Modal */}
            <SelectSafetyEmployeeModal
                open={safetyEmployeeModalOpen}
                onClose={() => setSafetyEmployeeModalOpen(false)}
                selectedEmployee={safetyEmployee}
                onSelect={async (employee) => {
                    setSafetyEmployee(employee);
                    setSafetyEmployeeModalOpen(false);

                    // Save immediately with new employee (preserve existing stepFive data)
                    const existingStepFive = jhaData?.stepFive || {};
                    const updated = {
                        ...jhaData,
                        stepFive: {
                            ...existingStepFive,
                            checklist,
                            checklistNotes,
                            comments,
                            safetyEmployee: employee,
                            completedAt: new Date().toISOString()
                        },
                        meta: {
                            ...(jhaData?.meta || {}),
                            lastStep: Math.max(jhaData?.meta?.lastStep || 1, 5)
                        }
                    };

                    const db = await initializeDB();
                    const tx = db.transaction(JHA_STORE, 'readwrite');
                    const store = tx.objectStore(JHA_STORE);
                    await store.put(updated);
                    await tx.done;

                    setJhaData(updated);
                    validateForm(updated);
                    window.dispatchEvent(new Event('jhaUpdated'));
                }}
            />

            {/* Safety Signature Modal */}
            <SignatureModal
                open={safetySignatureModalOpen}
                onClose={() => setSafetySignatureModalOpen(false)}
                existingSignature={safetySignature}
                employeeName={safetyEmployee?.FullName || 'Safety Department'}
                onSave={async (sig) => {
                    setSafetySignature(sig);
                    setSafetySignatureModalOpen(false);

                    // Save immediately with new signature (preserve existing stepFive data)
                    const existingStepFive = jhaData?.stepFive || {};
                    const updated = {
                        ...jhaData,
                        stepFive: {
                            ...existingStepFive,
                            checklist,
                            checklistNotes,
                            comments,
                            safetySignature: sig,
                            completedAt: new Date().toISOString()
                        },
                        meta: {
                            ...(jhaData?.meta || {}),
                            lastStep: Math.max(jhaData?.meta?.lastStep || 1, 5)
                        }
                    };

                    const db = await initializeDB();
                    const tx = db.transaction(JHA_STORE, 'readwrite');
                    const store = tx.objectStore(JHA_STORE);
                    await store.put(updated);
                    await tx.done;

                    setJhaData(updated);
                    validateForm(updated);
                    window.dispatchEvent(new Event('jhaUpdated'));
                }}
            />

            {/* Submit Confirmation Dialog */}
            <Dialog open={submitDialog} onClose={() => !submitting && setSubmitDialog(false)}>
                <DialogTitle>Submit JHA?</DialogTitle>
                <DialogContent>
                    <Typography>
                        Are you sure you want to submit this JHA? Once submitted, it cannot be edited.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setSubmitDialog(false)} disabled={submitting}>
                        Cancel
                    </Button>
                    <Button 
                        onClick={handleSubmit} 
                        variant="contained" 
                        color="primary"
                        disabled={submitting}
                        startIcon={submitting ? <CircularProgress size={20} /> : <SendIcon />}
                    >
                        {submitting ? 'Submitting...' : 'Submit'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
